import React, { useState, useEffect } from 'react';
import { ARTICLE_TO_LAB } from '../constants/labLinks';

// ── Diagram primitives ─────────────────────────────────────────────────────

const NODE_STYLES = {
  functionNode:   { bg: '#ede9fe', border: '#8b5cf6', icon: '⬡', label: 'Function' },
  gateNode:       { bg: '#fef3c7', border: '#f59e0b', icon: '◈', label: 'Gate'     },
  collectionNode: { bg: '#dbeafe', border: '#3b82f6', icon: '▦', label: 'Collection' },
  humanNode:      { bg: '#fce7f3', border: '#ec4899', icon: '○', label: 'Human'    },
  connectionNode: { bg: '#dcfce7', border: '#22c55e', icon: '⬢', label: 'Connection' },
  noteNode:       { bg: '#fef9c3', border: '#eab308', icon: '▭', label: 'Note'     },
};

function DiagramNode({ type, label, sublabel, x, y, onClick, active }) {
  const s = NODE_STYLES[type] || NODE_STYLES.functionNode;
  return (
    <g
      transform={`translate(${x},${y})`}
      style={{ cursor: 'pointer' }}
      onClick={() => onClick && onClick(type, label)}
    >
      <rect
        x={0} y={0} width={110} height={52} rx={10}
        fill={active ? s.border + '30' : s.bg}
        stroke={s.border}
        strokeWidth={active ? 2.5 : 1.5}
      />
      <text x={8} y={18} fontSize={13} fill={s.border} fontWeight="700">{s.icon}</text>
      <text x={26} y={18} fontSize={11} fill={s.border} fontWeight="700">{s.label}</text>
      <text x={8} y={35} fontSize={12} fill="#1e293b" fontWeight="600">{label}</text>
      {sublabel && <text x={8} y={48} fontSize={10} fill="#64748b">{sublabel}</text>}
    </g>
  );
}

function Arrow({ x1, y1, x2, y2, label }) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2 - 10;
  return (
    <g>
      <defs>
        <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
        </marker>
      </defs>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#arrow)" />
      {label && <text x={mx} y={my} fontSize={10} fill="#94a3b8" textAnchor="middle">{label}</text>}
    </g>
  );
}

// ── Diagram: Node type overview (clickable) ────────────────────────────────
function NodeTypeDiagram() {
  const [active, setActive] = useState(null);
  const nodes = [
    { type: 'functionNode',   label: 'Marketing', sublabel: 'task queue',  x: 10,  y: 10  },
    { type: 'gateNode',       label: 'Approval',  sublabel: 'Manager',     x: 140, y: 10  },
    { type: 'collectionNode', label: 'Tickets',   sublabel: '4 fields',    x: 270, y: 10  },
    { type: 'humanNode',      label: 'CEO',       sublabel: 'stakeholder', x: 10,  y: 80  },
    { type: 'connectionNode', label: 'Slack',     sublabel: 'connected',   x: 140, y: 80  },
    { type: 'noteNode',       label: 'Reminder',  sublabel: 'escalate 4h', x: 270, y: 80  },
  ];
  const info = {
    functionNode:   'A team\'s task queue you work manually. AI agents (with a playbook) are coming soon.',
    gateNode:       'Pauses the pipeline until a human approves the work.',
    collectionNode: 'A structured database — store tickets, contacts, records.',
    humanNode:      'A real person in the pipeline: stakeholder, requester, or decision-maker.',
    connectionNode: 'An external service — GitHub, Slack, webhook endpoints.',
    noteNode:       'A freeform annotation. Documents decisions and edge cases.',
  };
  return (
    <div className="uni-diagram-wrap">
      <svg viewBox="0 0 400 145" className="uni-svg">
        {nodes.map(n => (
          <DiagramNode key={n.type} {...n} active={active === n.type} onClick={t => setActive(t === active ? null : t)} />
        ))}
      </svg>
      {active && (
        <div className="uni-diagram-tooltip" style={{ borderColor: NODE_STYLES[active].border }}>
          <strong style={{ color: NODE_STYLES[active].border }}>{NODE_STYLES[active].icon} {NODE_STYLES[active].label}</strong>
          <span>{info[active]}</span>
        </div>
      )}
      {!active && <div className="uni-diagram-hint">Click any node to learn more</div>}
    </div>
  );
}

// ── Diagram: Customer support pipeline ────────────────────────────────────
function SupportPipelineDiagram() {
  const [active, setActive] = useState(null);
  const toggle = id => setActive(a => a === id ? null : id);
  const details = {
    human:    'The customer raises a support request. No AI here — just the entry point into the pipeline.',
    service:  'Service Desk agents triage tickets, auto-resolve FAQs, and escalate complex issues.',
    gate:     'Refunds over £500 pause here for manager approval before reaching Finance.',
    finance:  'Finance agents process approved refunds and update the ticket record.',
    tickets:  'All ticket data stored here: customer, issue, status, priority, resolution.',
  };
  return (
    <div className="uni-diagram-wrap">
      <svg viewBox="0 0 510 160" className="uni-svg">
        <Arrow x1={120} y1={36} x2={140} y2={36} />
        <Arrow x1={250} y1={36} x2={270} y2={36} />
        <Arrow x1={380} y1={36} x2={400} y2={36} />
        <Arrow x1={195} y1={62} x2={195} y2={95} label="writes" />
        <DiagramNode type="humanNode"      label="Customer"     x={10}  y={10} active={active==='human'}   onClick={() => toggle('human')}   />
        <DiagramNode type="functionNode"   label="Service Desk" sublabel="3 agents" x={140} y={10} active={active==='service'} onClick={() => toggle('service')} />
        <DiagramNode type="gateNode"       label="Refund Gate"  sublabel="manager" x={270} y={10} active={active==='gate'}    onClick={() => toggle('gate')}    />
        <DiagramNode type="functionNode"   label="Finance"      sublabel="1 agent"  x={400} y={10} active={active==='finance'} onClick={() => toggle('finance')} />
        <DiagramNode type="collectionNode" label="Tickets DB"   sublabel="status, priority" x={140} y={95} active={active==='tickets'} onClick={() => toggle('tickets')} />
      </svg>
      {active ? (
        <div className="uni-diagram-tooltip" style={{ borderColor: '#6366f1' }}>
          <strong>{active.charAt(0).toUpperCase() + active.slice(1)}</strong>
          <span>{details[active]}</span>
        </div>
      ) : <div className="uni-diagram-hint">Click any node for details</div>}
    </div>
  );
}

// ── Diagram: DevOps CI/CD pipeline ────────────────────────────────────────
function DevOpsPipelineDiagram() {
  const [active, setActive] = useState(null);
  const toggle = id => setActive(a => a === id ? null : id);
  const details = {
    github:   'Monitors PRs tagged "ready-for-deploy". Sends webhook events on build completion.',
    dev:      'Agents review PRs, run linters, write tests, and update the dev tickets collection.',
    gate:     'Tech lead must approve: tests passing + no critical lint errors.',
    devops:   'Triggers the build pipeline, deploys to staging, then production after gate approval.',
    deploy:   'The target environment — a webhook endpoint, a cloud provider, or a staging server.',
    tickets:  'Dev tickets: PR status, assignee, link to PR, test results.',
  };
  return (
    <div className="uni-diagram-wrap">
      <svg viewBox="0 0 550 160" className="uni-svg">
        <Arrow x1={120} y1={36} x2={140} y2={36} />
        <Arrow x1={250} y1={36} x2={270} y2={36} />
        <Arrow x1={380} y1={36} x2={400} y2={36} />
        <Arrow x1={450} y1={62} x2={450} y2={95} />
        <Arrow x1={195} y1={62} x2={195} y2={95} label="writes" />
        <DiagramNode type="connectionNode" label="GitHub"       sublabel="webhook" x={10}  y={10} active={active==='github'}  onClick={() => toggle('github')}  />
        <DiagramNode type="functionNode"   label="Development"  sublabel="2 agents" x={140} y={10} active={active==='dev'}     onClick={() => toggle('dev')}     />
        <DiagramNode type="gateNode"       label="Code Review"  sublabel="tech lead" x={270} y={10} active={active==='gate'}    onClick={() => toggle('gate')}    />
        <DiagramNode type="functionNode"   label="DevOps"       sublabel="2 agents" x={400} y={10} active={active==='devops'}  onClick={() => toggle('devops')}  />
        <DiagramNode type="connectionNode" label="Deploy"       sublabel="webhook"  x={400} y={95} active={active==='deploy'}  onClick={() => toggle('deploy')}  />
        <DiagramNode type="collectionNode" label="Dev Tickets"  sublabel="4 fields" x={140} y={95} active={active==='tickets'} onClick={() => toggle('tickets')} />
      </svg>
      {active ? (
        <div className="uni-diagram-tooltip" style={{ borderColor: '#22c55e' }}>
          <strong>{active.charAt(0).toUpperCase() + active.slice(1)}</strong>
          <span>{details[active]}</span>
        </div>
      ) : <div className="uni-diagram-hint">Click any node for details</div>}
    </div>
  );
}

// ── Diagram: Content marketing pipeline ───────────────────────────────────
function MarketingPipelineDiagram() {
  const [active, setActive] = useState(null);
  const toggle = id => setActive(a => a === id ? null : id);
  const details = {
    director: 'Marketing Director raises a brief: topic, target audience, key message.',
    marketing: 'Copywriter and scheduler agents draft posts, select images, schedule timing.',
    gate:     'Director reviews the draft. Auto-approves after 48h if no action taken.',
    buffer:   'Approved posts published to Buffer/Instagram via webhook.',
  };
  return (
    <div className="uni-diagram-wrap">
      <svg viewBox="0 0 430 80" className="uni-svg">
        <Arrow x1={120} y1={36} x2={140} y2={36} />
        <Arrow x1={250} y1={36} x2={270} y2={36} />
        <Arrow x1={380} y1={36} x2={400} y2={36} />
        <DiagramNode type="humanNode"      label="Director"     sublabel="brief"    x={10}  y={10} active={active==='director'}  onClick={() => toggle('director')}  />
        <DiagramNode type="functionNode"   label="Marketing"    sublabel="3 agents" x={140} y={10} active={active==='marketing'} onClick={() => toggle('marketing')} />
        <DiagramNode type="gateNode"       label="Approval"     sublabel="48h auto" x={270} y={10} active={active==='gate'}      onClick={() => toggle('gate')}      />
        <DiagramNode type="connectionNode" label="Buffer"       sublabel="publish"  x={400} y={10} active={active==='buffer'}    onClick={() => toggle('buffer')}    />
      </svg>
      {active ? (
        <div className="uni-diagram-tooltip" style={{ borderColor: '#f59e0b' }}>
          <strong>{active.charAt(0).toUpperCase() + active.slice(1)}</strong>
          <span>{details[active]}</span>
        </div>
      ) : <div className="uni-diagram-hint">Click any node for details</div>}
    </div>
  );
}

// ── Diagram: Schedule types ────────────────────────────────────────────────
function ScheduleDiagram() {
  const schedules = [
    { label: 'Triggered only', sub: 'Wakes on demand', color: '#6366f1', icon: '⚡', cost: '💰' },
    { label: 'Custom (M–F 9–5)', sub: 'Business hours', color: '#22c55e', icon: '⏱', cost: '💰💰' },
    { label: '24/7 Always on', sub: 'Continuous',     color: '#ef4444', icon: '🔴', cost: '💰💰💰' },
  ];
  return (
    <div className="uni-diagram-wrap">
      <div className="uni-sched-grid">
        {schedules.map(s => (
          <div key={s.label} className="uni-sched-card" style={{ borderColor: s.color }}>
            <div className="uni-sched-icon" style={{ background: s.color + '22', color: s.color }}>{s.icon}</div>
            <div className="uni-sched-name" style={{ color: s.color }}>{s.label}</div>
            <div className="uni-sched-sub">{s.sub}</div>
            <div className="uni-sched-cost">{s.cost}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Diagram: Model comparison ──────────────────────────────────────────────
function ModelDiagram() {
  const models = [
    { name: 'Haiku 4.5',  icon: '⚡', color: '#22c55e', speed: 95, cost: 10,  power: 40,  use: 'FAQ, triage, simple tasks' },
    { name: 'Sonnet 4.6', icon: '◈',  color: '#6366f1', speed: 75, cost: 30,  power: 75,  use: 'Most business functions' },
    { name: 'Opus 4.8',   icon: '★',  color: '#f59e0b', speed: 45, cost: 100, power: 100, use: 'Complex reasoning, code review' },
  ];
  return (
    <div className="uni-diagram-wrap">
      <div className="uni-model-grid">
        {models.map(m => (
          <div key={m.name} className="uni-model-card" style={{ borderColor: m.color }}>
            <div className="uni-model-head">
              <span className="uni-model-icon" style={{ color: m.color }}>{m.icon}</span>
              <span className="uni-model-name" style={{ color: m.color }}>{m.name}</span>
            </div>
            <div className="uni-model-bars">
              <div className="uni-bar-row">
                <span>Speed</span>
                <div className="uni-bar-bg"><div className="uni-bar-fill" style={{ width: m.speed + '%', background: m.color }} /></div>
              </div>
              <div className="uni-bar-row">
                <span>Power</span>
                <div className="uni-bar-bg"><div className="uni-bar-fill" style={{ width: m.power + '%', background: m.color }} /></div>
              </div>
              <div className="uni-bar-row">
                <span>Cost</span>
                <div className="uni-bar-bg"><div className="uni-bar-fill" style={{ width: m.cost + '%', background: '#ef4444' }} /></div>
              </div>
            </div>
            <div className="uni-model-use">{m.use}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Article data ───────────────────────────────────────────────────────────
const ARTICLES = [
  {
    id: 'gs-1', category: 'Getting Started', title: 'What is Projectly?',
    diagram: <NodeTypeDiagram />,
    content: `Projectly is a workflow automation platform. You build pipelines — visual graphs of connected nodes — that model how work flows through your organisation, and the server runs the automation for you.

⚠️ **What works today vs. coming soon:** The **automation engine is live** — Collections, Triggers, Conditions, Actions, Gates, Connections, Notes, Schedules and the REST API all work now. **AI agents** (Function nodes that read a playbook and act on their own) are **not yet enabled — they're coming soon.** Anywhere this guide mentions agents, playbooks, "Generate with AI", or Buzz AI, treat it as an upcoming capability, not something you can use today.

**Core concept:** You describe your data (Collections) and the rules for acting on it (Triggers → Conditions → Actions); the server watches for changes and runs your Actions automatically — no code required.

**Two views:**
- **Pipeline view** — the technical blueprint (Functions, Gates, Collections, Connections)
- **Operations view** — the same pipeline with business-friendly labels (Departments, Approvals, Records, Apps)`
  },
  {
    id: 'gs-2', category: 'Getting Started', title: 'Your first pipeline (5 min)',
    content: `Build a simple automation with **no AI agents**: when a contact is added, you get a notification. This is the quickest way to see a working pipeline end-to-end.

**Step 1 — Add a Collection node**
Add a Collection node (use the toolbar Add buttons at the top of the canvas, or right-click the canvas → Add Collection). Open it and, on the **Fields** tab, add two fields: **Name** (text) and **Email** (text). Name the collection "Contacts".

**Step 2 — Add a Trigger node**
Add a Trigger node. In its right panel set **Trigger type = Record added** and **Collection = Contacts**.

**Step 3 — Add an Action node**
Add an Action node. Set **Action type = Send message**. Write a subject and message — you can insert the new record's values with curly braces, e.g. \`Welcome {name}\`.

**Step 4 — Connect them**
Drag from the right handle of each node to the left handle of the next: **Contacts → Trigger → Action**.

**Step 5 — Turn the pipeline on**
Click **▶ Run** in the top toolbar. The button shows **⏹ Stop** when the pipeline is active. Triggers only fire while the pipeline is active.

**Step 6 — Add a record (this fires it)**
Open the **Contacts** collection → **Records** tab → **+ Add record**. Type a Name, then click the green **✓** tick to save. Records are staged and only saved (and only fire the trigger) when you click Save — so a half-typed row never runs the pipeline.

**Step 7 — Check your Inbox**
Open the **Inbox** (left sidebar). You'll see the notification your Action sent. That's a full automation: Collection → Trigger → Action → Inbox.`
  },
  {
    id: 'gs-3', category: 'Getting Started', title: 'Understanding the toolbar',
    content: `The toolbar at the top of the pipeline canvas has several key controls:

**Add buttons** — quickly add any node type to the canvas

**📊 Token usage / 💰 Show costs** — toggle a real-time overlay showing how many tokens each node has consumed, or the estimated cost in USD

**▶ Run / ⏹ Stop** — start or stop the entire pipeline. Individual functions can still be toggled independently via the Schedule panel.

**⏱ Schedule** — opens the Execution & Schedules panel on the right. Here you set the global pipeline schedule and per-function overrides.

**✓ Saved / ⟳ Saving…** — auto-save indicator. Changes are persisted to Firestore automatically after 1.2 seconds of inactivity.`
  },

  {
    id: 'tb-1', category: 'Data', title: 'Data — your data workspace',
    content: `**Data** (🗂️ in the sidebar, Build group) is where ALL your data lives day-to-day — every collection in your workspace, whether it was created here, on the pipeline canvas, by a form, or via the API. Each appears as a table you can work in like a spreadsheet — no pipeline canvas required.

**One set of data, two places to see it.** Data and the canvas show the *same* collections and records. A collection you create on the canvas appears in Data instantly, and a table you create in Data appears on the canvas as a Collection node. Records added in either place fire the same triggers — so your automations keep working no matter where you edit.

**The grid**
- **Add a record:** click **+ Add record**, fill the draft row, then click the green **✓** to save. (Records only save — and only fire triggers — on that explicit Save.)
- **Edit inline:** click any cell and type; changes save as you go.
- **Sort:** click a column header (again to reverse, a third time to reset).
- **Search:** the search box filters across every field.
- **Link or delete:** right-click a row, exactly like the canvas grid.

**Record detail**
Click a row's number (or the ↗ button) to open the detail panel: every field stacked and editable, plus created/updated timestamps — and a note when a pipeline made the last change.

**Creating a table**
Type a name in the left panel and press Enter. Then open **⚙️ Fields** to define columns — Text, Number, Select (dropdowns like Status), Date, or Relation (link to another table).

**Fields**
The **⚙️ Fields** button edits the table's schema: rename fields, change types, add dropdown options, or delete fields. Deleting a field hides its column; the values stay on the records.

💡 **Where pipelines come in:** Data is the *what*; pipelines are the *when/then* (your rules). A record added or changed in Data can send messages, update fields, create tasks, or wait at an approval gate — see **Your first pipeline (5 min)** under Getting Started.`
  },
  {
    id: 'tb-3', category: 'Data', title: 'Views — saved filters & kanban boards',
    content: `A **view** is a saved way of looking at a table: which rows (filters), which columns (visible fields), what order (sort) — and whether it's a **grid** or a **kanban board**. Views live in the tab row above the table; **All records** is always there, and each saved view sits beside it.

**Creating a view**
Click **＋ View** in the tab row. Give it a name, pick a layout, optionally add filters and untick fields you want hidden, then save. Double-click a view tab (or click **✎**) to edit it; deleting a view never touches your records.

**Filters**
Each filter is *field · operator · value* — e.g. *Status is Open*, *Amount > 100*, *Email is not empty*. All filters must match (AND). Great for "My open tickets", "High-value orders", "Missing contact info".

**Kanban boards** 📌
Choose the **Kanban** layout and a **Select field** to group by (Status, Priority, Stage…). Each option becomes a column; records become cards:
- **Drag a card** between columns (or use the card's "Move to…" dropdown) — this *actually updates the field* on the record.
- Because it's a real field change, **field-change triggers fire** — drag "deal" to "Won" and your pipeline can send the welcome email. One drag = one automation run.
- **Double-click a card** to open its record detail.
- Records with no value in the group-by field collect in a **(none)** column.

**Run a pipeline on one record** ▶
Open a record's detail panel: if the pipeline has a **Manual trigger** wired to this collection, you'll see a **Run** button — it executes the pipeline *against that specific record* (so \`{field}\` placeholders resolve from it). Perfect for "re-send this one" or "process just this row".

**⚡ Automate**
The toolbar's **Automate** button jumps to the Pipeline canvas — add a Trigger watching this collection (record added / field change) and connect Actions to put the table to work.

_Try the **"Boards — drag a card, fire a pipeline"** lab for a 5-minute hands-on tour._`
  },
  {
    id: 'tb-4', category: 'Data', title: 'Online forms — collect data from anyone',
    content: `Turn any table into a **shareable online form**: people open a link (no login), fill it in, and each submission becomes a **record in your table** — which means your pipelines run on it automatically.

**Creating a form**
Open the table → **🔗 Forms** → **+ New form**. Choose a title, an optional description and thank-you message, and which fields appear (edit their labels, tick which are required). Click **Create form** — you get a public link to copy or open.

**The link is the key** 🔑
Each form URL contains a long random key. Anyone with the full link can submit; nobody can guess it. Treat it like a capability: share it where you need submissions from, and if it leaks, **Close** the form (the link stops working instantly — reopen any time) or delete it and create a new one.

**What happens on submit**
1. The visitor's answers are validated (required fields, only the fields you exposed).
2. A **record is created** in the table, marked as coming from the form.
3. Any **record added** trigger watching the collection fires — send an email, set a status, route by priority, await an approval… "what the form does" is just your pipeline.

**Program it: the form + conditions pattern**
Add a *Priority* select to the form, then in your pipeline: Trigger (record added) → **Condition** (priority is High) → YES: send email / NO: in-app message. That's a complete support-intake flow with zero code.

**Spam & abuse protection**
Forms include a hidden honeypot (bots that fill it are silently dropped) and per-form rate limiting (30 submissions/minute). Bulk imports and CSVs don't come through forms, so every form submission is a genuine event.

**For developers** 👩‍💻
The same URL accepts \`POST\` with \`Content-Type: application/json\` — body keys are the field ids, response is \`{ "ok": true, "recordId": "…" }\`. That means a customer's own website form can submit straight to your table with one fetch call. (For richer integrations use the REST API — see *External API*.)

_Try the **"Forms — a public URL that runs your pipeline"** lab for the guided version._`
  },
  {
    id: 'tb-2', category: 'Data', title: 'CSV import & export',
    content: `Move data in and out of any table as CSV — the fastest way to get an existing spreadsheet into Projectly.

**Importing**
1. Open the table, click **📥 Import CSV** and pick your file. The first row must be column headers.
2. A mapping screen appears showing each CSV column with an example value. For every column choose:
   - an **existing field** (columns whose header matches a field name are pre-matched automatically),
   - **＋ Create new field** — adds a text field named after the column, or
   - **— skip —** to leave that column out.
3. Click **Import**. Rows with no values in the mapped columns are skipped, so stray empty lines never become blank records.

⚠️ **Imports don't fire triggers.** Bulk-imported rows are marked as backfill, so importing 500 rows will *not* start 500 pipeline runs. Records you add one-by-one in the grid *do* fire triggers as normal. (If you want automation to run on imported data, add records individually or update a field on them afterwards.)

**Exporting**
Click **📤 Export** to download the table as a CSV named after the table — one column per field, in the same format Import accepts. Handy for backups, Excel analysis, or moving data between workspaces.

**Format notes**
- Values containing commas, quotes or line breaks are quoted correctly on export and understood on import.
- Relation fields export the linked record's id.`
  },

  {
    id: 'nt-1', category: 'Node Types', title: 'Function Node — team & task queue',
    diagram: <NodeTypeDiagram />,
    content: `A **Function Node** (a Department in Operations view) groups work for a team — Marketing, Service Desk, Finance, DevOps, Sales — and holds a **task queue**: tasks with fields and statuses your team works through.

⚠️ **AI agents are not yet enabled.** Today a Function node is a **human task queue** — you and your team create and work its tasks manually, and Actions can add tasks to it automatically. The **Agents**, **Playbook** and **model selection** described below are the **coming-soon AI layer** (agents reading a playbook and working tasks for you). They have no effect until AI is turned on.

**What works today:**
- **Name / Description** — the function's identity, shown on the canvas
- **Task queue** — open the node to add tasks, set fields (e.g. Status, Priority), and move them to Done. The **Field change → function task** trigger can fire when a task field changes, and the **Create function task** Action can add tasks here.

**Coming soon (AI — not active yet):**
- **Agents** — AI workers with a name, role, model and instructions
- **Playbook** — the operating manual agents read before acting
- **Model selection** — Efficient (Haiku) / Balanced (Sonnet) / Expert (Opus) per agent`
  },
  {
    id: 'nt-2', category: 'Node Types', title: 'Gate Node — approval checkpoint',
    content: `A **Gate Node** (Approval in Operations view) pauses the pipeline until a human (or rule) approves the work.

**When to use:** Content review before publishing, budget sign-off, legal review, code review before deploy.

**Configuration (right panel → Approval rules):**
- **Approver** — who must approve (e.g. "Marketing Manager", "CTO")
- **Approval criteria** — conditions that must be met (e.g. "All copy must be brand-compliant")
- **Auto-approve after** — optional timeout: approve automatically if no action after 24/48/72h

When a gated pipeline reaches the gate it **pauses** and drops an approval request in your **Inbox**; approving it there resumes the pipeline.`
  },
  {
    id: 'nt-3', category: 'Node Types', title: 'Collection Node — structured data',
    content: `A **Collection Node** (Records in Operations view) is a structured database living directly on your pipeline canvas. Agents can read from and write to it during execution.

**When to use:** Support tickets, lead lists, bug trackers, content calendars, inventory.

**Structure:**
- **Fields** — define the schema: text, number, date, select (dropdown), agent (assigned person), reference (link to another collection)
- **Records** — the actual rows of data

**Adding records:** On the **Records** tab, click **+ Add record**, fill the row, then click the green **✓** tick to save. Records are *staged* — they're only written (and only fire a Record-added trigger) when you click Save, so a half-filled row never runs your pipeline by accident.

**Right-click a record** to assign it to an agent, escalate it to another collection, or delete it.

**Tip:** Add a "Status" field of type select with options like Open / In Progress / Done to get the coloured status chips displayed on the canvas.

💡 **Prefer a spreadsheet feel?** Every collection is also available in the **🗂️ Data** section (see *Data*), where you can edit records in a full-page grid, search/sort, open a record's detail panel, and import/export CSV — same data, same triggers.`
  },
  {
    id: 'nt-4', category: 'Node Types', title: 'Human Node — stakeholder input',
    content: `A **Human Node** (Stakeholder in Operations view) represents a real person in the pipeline — typically someone who initiates requests, reviews output, or makes final decisions.

**When to use:** Business owner who raises requests, a client who approves deliverables, an executive who signs off.

**Right-click → Direct message** opens a chat with the Human node for context.

**Right-click → View tasks / Raise task** to see or create tasks for this person.

**Note:** Human nodes don't run AI agents. They're signposts for human touchpoints in the flow.`
  },
  {
    id: 'nt-5', category: 'Node Types', title: 'Connection Node — external apps',
    content: `A **Connection Node** (App in Operations view) represents an external service your pipeline integrates with — GitHub, Slack, Gmail, Jira, a webhook endpoint, etc.

**When to use:** Any point where your pipeline sends or receives data from outside the platform.

**Configuration (right-click → Configure connection or select → right panel):**
- **Service type** — GitHub, Slack, Jira, Gmail, Notion, Airtable, Linear, Webhook, Custom
- **Endpoint / URL** — the API endpoint or webhook URL
- **Auth token** — Bearer token or API key (stored in the node data; for production, use the Connections portal for secure secret storage)

**Coming soon:** Full OAuth flows and live status indicators via the Connections portal.`
  },
  {
    id: 'nt-6', category: 'Node Types', title: 'Note Node — annotations',
    content: `A **Note Node** (Memo in Operations view) is a freeform sticky note on the canvas. It doesn't execute anything — it's purely for documentation.

**When to use:**
- Document a business rule that applies to nearby nodes
- Flag an issue or decision for the team
- Leave onboarding context for new team members

**Features:**
- 6 background colours (right-click → colour picker)
- Title + body text
- Pin to keep at top of canvas (right-click → Pin note)

**Tip:** Use notes to document escalation rules between nodes — e.g. "If a tier-1 ticket is unresolved for 4 hours, escalate to Development"`
  },

  {
    id: 'au-1', category: 'Automation', title: 'How automations work',
    content: `Automations let your pipeline react to data changes **without any agent or human in the loop**. They are built from three node types working together:

**⚡ Trigger → ◇ Condition → ⚙ Action**

1. A **Trigger** fires when something happens (a record is added, or a field changes).
2. An optional **Condition** checks the record and routes execution down a YES or NO path.
3. One or more **Actions** then do the work — update a field, create a record, or send a message.

**Server-side execution:** Automations run on the server (Cloud Functions), not in your browser. They keep working even when no one has the app open — including triggers fired by the external REST API.

**The pipeline must be active (▶ Run).** Triggers only fire while the pipeline is in the Run/active state. If you reload the page or the pipeline is paused, automations stop firing until you hit ▶ Run again — so confirm it shows as active before testing.

**Build one:** Drop a Trigger, a Condition, and an Action on the canvas, wire them left-to-right, then activate the pipeline with ▶ Run.`
  },
  {
    id: 'au-2', category: 'Automation', title: 'Trigger Node — start an automation',
    content: `A **Trigger Node** (Automation Start in Operations view) is the entry point of an automation. It watches a collection and fires when a matching event occurs.

**Trigger types:**
- **Manual** — only fires when you click Run on the node (or call \`runManualTrigger\`). Good for testing.
- **Record added** — fires whenever a new record is created in the chosen collection (including via the REST API).
- **Field change** — fires when a specific field changes value. Optionally match a target value (e.g. only when Status becomes "Escalated").

**Configuration (right panel):**
- **Trigger type** — manual / record_added / field_change
- **Collection** — which collection to watch
- **Field** + **Value** — for field_change, which field and (optionally) which value to match

**Note:** Triggers only fire while the pipeline is active. Writes stamped by the workflow engine itself never re-trigger, so automations can't loop infinitely.`
  },
  {
    id: 'au-3', category: 'Automation', title: 'Condition Node — branching logic',
    content: `A **Condition Node** (Decision in Operations view) evaluates the triggering record and routes execution down one of two paths.

**Two output handles:**
- **YES** (green) — taken when the condition matches
- **NO** (red) — taken when it doesn't

**Configuration (right panel):**
- **Conditions** — one or more field comparisons (equals, not equals, contains, greater than, less than, is empty…)
- **Logic** — combine multiple conditions with AND or OR

**Example:** \`Priority equals "Critical"\` AND \`Status equals "Open"\` → route to an urgent-escalation Action on the YES path, and a normal-queue Action on the NO path.

**Tip:** Chain conditions to build decision trees — the NO path of one condition can feed into another condition.`
  },
  {
    id: 'au-4', category: 'Automation', title: 'Action Node — do the work',
    content: `An **Action Node** (Auto Step in Operations view) performs an automated operation when reached.

**Action types:**
- **Update field** — set a field on the triggering record (or records matching a filter)
- **Create record** — add a new record to a collection, optionally mapping values from the trigger
- **Send message** — notify someone via Inbox, email, or WhatsApp (recipient can come from a record field)
- **Function task** — add the record to a Function node's task queue for your team to work (AI agents to auto-work these are coming soon)
- **HTTP request** — call any external URL (GET/POST/PATCH/…) through a Connection. Supports Bearer, API-key, and Basic auth headers. This is how you talk to any third-party API today, including GitHub. Because external calls can be flaky, this action has **Timeout**, **Retries**, and **"Keep the pipeline running if this call fails"** options — handy when the other system is occasionally slow or down.
- **GitHub** _(coming soon)_ — a one-click issue/PR/commit action. For now, use **HTTP request** with a GitHub connection.

**Which records does Update field touch?**
- **No filter** → only the **single record that triggered the run**. (It no longer overwrites every record by default.)
- **With a filter** (field = value) → every record in the target collection matching that filter.

**Pick the target collection two ways:** wire an edge from the Action to a Collection node, **or** choose it from the Collection dropdown in the Action's panel. Either works — you no longer need both.

**Using record data in your values — \`{tokens}\`:** Any value field (a message body, a URL, an update value) can pull in live data with \`{field_name}\` tokens. Field names are lower-cased and spaces become underscores, so **"Customer Email"** becomes \`{customer_email}\`. Example body: \`Order from {customer_email} is now {status}\`.

**When something fails, check Logs.** Every run is recorded in the Logs page with a per-step status. An **unreachable Connection URL hard-fails the action** (the run stops at that step) — Logs is where you'll see the exact error.`
  },

  {
    id: 'ts-1', category: 'Troubleshooting', title: 'My automation didn\'t run — checklist',
    content: `Most "nothing happened" moments come down to one of a handful of causes. Work down this list — it's ordered by how often each one is the culprit.

**1. Is the pipeline active?**
Automations only fire while the pipeline is in the **▶ Run / active** state. If you reloaded the page, switched workspaces, or paused it, triggers stop firing until you hit **▶ Run** again. This is the single most common cause — confirm it shows active before testing.

**2. Did you actually save the record?**
Adding a record stages a **draft row**. The trigger fires on the green **✓** tick to save, not while you type. A half-filled row that was never saved never runs the pipeline.

**3. Does the trigger match what changed?**
- **Record added** fires only on a *new* record — editing an existing one won't fire it.
- **Field change** fires only when *that specific field* changes, and (if you set a target value) only when it changes *to that value*. Changing a different field does nothing.

**4. Is the Action pointed at a collection?**
An **Update field** action needs a target collection — either an **edge** to the Collection node **or** the **Collection dropdown** in the action's panel. With neither, it can't write and logs an error.

**5. Give it a second.**
Automations run on the server (Cloud Functions), so a change lands **1–2 seconds** after you save — not instantly. The record modal updates live (the row flashes when it changes); just don't expect it the same millisecond.

**Still stuck?** Open **Logs** — every run is recorded there with a per-step status and the exact error. See *"Reading the Logs page"*.`
  },
  {
    id: 'ts-2', category: 'Troubleshooting', title: 'Reading the Logs page',
    content: `**Logs** is your single source of truth for what the server actually did. Every pipeline run writes an entry here, whether it succeeded or failed.

**Each run shows:**
- **Which trigger fired** and the record that set it off
- **Each step in order** with a **status** — completed, skipped (a condition routed around it), or **failed**
- The **exact error message** on any failed step

**Common errors and what they mean:**

| Log message | Cause | Fix |
|-------------|-------|-----|
| \`update_field action is not connected to a collection\` | The action has no target | Draw an edge to the Collection, or pick it in the action's dropdown |
| \`fetch failed\` / \`Failed: <url>\` | An HTTP request hit an unreachable or wrong URL | Check the Connection URL; an unreachable URL hard-fails the whole run |
| A value printed literally as \`{customer_email}\` | A templating token that didn't resolve | Field tokens are lower-cased with spaces as underscores — "Customer Email" → \`{customer_email}\` |
| Step shows **skipped** | A Condition routed down the other branch | Check the condition's logic and the record's values |

**Tip:** If a run isn't in Logs at all, the trigger never fired — go back to the *"My automation didn't run"* checklist (the pipeline probably wasn't active).`
  },

  {
    id: 'pb-1', category: 'Writing Agent Playbooks (coming soon)', title: 'What makes a great playbook',
    content: `⚠️ **Not yet available.** AI agents aren't enabled yet, so playbooks don't do anything today — this whole section is a preview of the coming AI layer. You can still draft playbooks now so they're ready when agents go live.

The **Playbook** (Handbook in Operations view) is the most important thing you'll configure on a Function node once AI is enabled. It's the system prompt your agents receive before every task.

**A great playbook has 5 parts:**

**1. Role definition**
"You are the Marketing Manager for Acme Ltd. You oversee social media content, email campaigns, and brand messaging."

**2. Core responsibilities**
"Your main tasks are: reviewing content drafts, scheduling posts, briefing the copywriter, and reporting weekly metrics."

**3. Decision rules**
"If a content piece mentions pricing, it must go to the Gate: Content Approval before publishing. Never publish directly."

**4. Escalation paths**
"If a customer complaint requires a refund over £500, escalate to the Business Owner node immediately."

**5. Tone & communication style**
"Always respond in a professional, friendly tone. Keep responses under 3 paragraphs unless asked for detail."`
  },
  {
    id: 'pb-2', category: 'Writing Agent Playbooks (coming soon)', title: 'Example: Service Desk playbook',
    content: `⚠️ **Not yet available** — AI agents aren't enabled yet. Example playbook for when they are:

\`\`\`
You are the Service Desk Manager for [Company] IT support.

RESPONSIBILITIES:
- Triage all incoming support tickets
- Assign tickets to the correct tier based on severity
- Escalate unresolved tickets after 4 hours
- Send status updates to users every 2 hours for open tickets

TICKET SEVERITY LEVELS:
- Critical: Production system down, security breach → escalate to Development immediately
- High: Key feature broken, affects >10 users → assign to senior tech, target 2h resolution
- Medium: General bugs, slow performance → assign to Tier-1, target 8h resolution
- Low: How-to questions, cosmetic issues → self-serve docs or Tier-1, target 24h

ESCALATION RULES:
- After 4 hours unresolved → notify manager
- After 8 hours unresolved → escalate to Development
- Any ticket involving customer data or billing → flag to Business Owner immediately

COMMUNICATION:
- Always acknowledge tickets within 15 minutes
- Use clear, non-technical language when communicating with end users
- Document all actions taken in the ticket record
\`\`\``
  },
  {
    id: 'pb-3', category: 'Writing Agent Playbooks (coming soon)', title: 'Example: DevOps function playbook',
    content: `⚠️ **Not yet available** — AI agents aren't enabled yet. Example playbook for when they are:

\`\`\`
You are the DevOps Manager responsible for CI/CD pipelines, infrastructure, and deployments.

RESPONSIBILITIES:
- Monitor all deployment requests from the Development function
- Review code before triggering builds
- Manage the deployment pipeline: build → test → staging → production
- Alert on failed builds or performance degradation

DEPLOYMENT RULES:
- All deployments require a passing test suite (check GitHub Actions status)
- Production deployments require approval from the Gate: Deploy Approval
- Hotfixes can bypass staging with explicit Business Owner sign-off
- Rollbacks can be triggered without approval if error rate exceeds 5%

INTEGRATIONS:
- GitHub: monitor PRs tagged "ready-for-deploy"
- Webhook: receive build completion events
- Slack: send deployment notifications to #deploys channel

DO NOT:
- Deploy directly to production without gate approval
- Merge to main without at least one reviewer
- Ignore failing health checks post-deploy
\`\`\``
  },

  {
    id: 'cn-1', category: 'Connections', title: 'How connections work',
    content: `Connections let your pipeline interact with external services — GitHub, Slack, Jira, etc.

**There are two ways to configure a connection:**

**1. Via the Connection Node on canvas**
Right-click a Connection Node → Configure connection (or select it and use the right panel). Set the service type, endpoint URL, and auth token. This is stored in the node data and is good for documenting what a connection represents.

**2. Via the Connections portal (sidebar)**
The Connections portal stores credentials securely using Firebase Secret Manager. This is the right place for production API keys and OAuth tokens. Credentials stored here are encrypted at rest and never exposed to the frontend.

**Coming soon:**
- OAuth flows for GitHub, Slack, Google Workspace
- Webhook receiver endpoints
- Live connection status (green/red indicator on canvas)`
  },
  {
    id: 'cn-3', category: 'Connections', title: 'Sharing a collection with another workspace',
    content: `⚠️ **Coming soon — not yet available.** This describes how cross-workspace collections will work so you can plan for them. The Collection Connector node does not exist in the pipeline toolbar yet.

**The problem it solves**

A workspace is a container: it owns one pipeline, its collections, its members, and its projects. That boundary is deliberate — two businesses should not share a member list or a billing line.

But sometimes one person works across both. A project manager running two companies may need a single project that draws on data from each. Duplicating the data into a second workspace means it goes stale immediately, and it means copying records that were never yours to copy.

**How it will work**

Rather than sharing a project between workspaces, you share a *collection* — through a connector node, exactly like connecting GitHub.

**Step 1 — Request access**
In workspace A, add a **Collection Connector** node and pick the workspace you want to read from. This sends a request to that workspace's owner. Nothing is shared yet.

**Step 2 — The other owner approves, and chooses what to share**
The owner of workspace B sees the request in their Inbox. They approve or decline. If they approve, they choose:
- **Which collection** — not the whole workspace, one named collection.
- **Which fields** — e.g. share \`id\`, \`status\`, \`title\` from Tickets but not \`customerEmail\`.
- **Read or read/write** — read-only by default.

**Step 3 — Use it like any other collection**
The connector node behaves like a collection in workspace A's pipeline. Triggers can watch it; conditions can branch on it; actions can update a task from it.

**What does NOT cross the boundary**

This is the part worth reading twice:
- **Members do not.** People in workspace B get no access to workspace A, or to any project in it. Nothing is granted by association.
- **Compute and billing do not.** A pipeline in workspace A runs on A's budget. Nothing of B's executes on A's behalf.
- **Projects do not.** A project always belongs to exactly one workspace. Data is what travels, never ownership.

**Revoking**

The owner of workspace B can revoke access at any time. The connector then fails visibly in A's pipeline logs rather than quietly returning stale or empty data — you will know it stopped.

**Worked example — one manager, two companies**

Ula Goods and Acme Ltd are separate workspaces with separate teams. Priya manages a joint fulfilment project, which lives in Ula Goods.

1. In Ula Goods, Priya adds a Collection Connector pointing at Acme.
2. Acme's owner approves read access to **Shipments**, sharing only \`reference\`, \`status\` and \`eta\` — not customer details.
3. A trigger in Ula Goods watches Shipments for \`status → Delivered\`.
4. That trigger updates the matching task in the fulfilment project and notifies Priya.

Acme's team never sees Ula Goods' project. Ula Goods never sees Acme's customer data. Priya sees one project with everything she needs in it.

**Why not just share the project?**

It was considered and rejected. Co-owning a project across two workspaces means both member lists get access to everything in it, there is no single answer to whose inbox an item belongs in, and no way to attribute the cost of a run. Sharing a scoped, approved, revocable collection gives you the same result with none of that.`
  },
  {
    id: 'cn-2', category: 'Connections', title: 'Connecting GitHub manually',
    content: `Until the full OAuth flow is built, you can connect GitHub manually:

**Step 1 — Create a GitHub Personal Access Token**
Go to github.com/settings/tokens → Generate new token (classic)
Scopes needed: \`repo\`, \`workflow\`, \`read:org\`

**Step 2 — Add a Connection Node to your pipeline**
Add a Connection Node, right-click → Configure connection
- Service type: GitHub
- Endpoint: \`https://api.github.com\`
- Auth token: paste your PAT

**Step 3 — Reference it in your Function playbook**
In your DevOps function playbook, reference the connection:
"Use the GitHub connection to check PR status at https://api.github.com/repos/{owner}/{repo}/pulls"

**Step 4 — Store securely (optional)**
For production, paste the token in the Connections portal (sidebar → Connections) to store it encrypted in Secret Manager.`
  },

  {
    id: 'sc-1', category: 'Schedules', title: 'Pipeline and function schedules',
    diagram: <ScheduleDiagram />,
    content: `What works today: a **Trigger node set to "Schedule"** fires its pipeline automatically on a repeating interval (handled server-side). The per-function schedule settings below govern when a function's **AI agents** are active — that part is **coming soon** (agents aren't enabled yet).

**Three schedule types:**
- **Triggered only** — function wakes up only when something explicitly triggers it (a webhook, a manual run, or another function passing work to it). Most cost-efficient.
- **Custom schedule** — active on specific days and between set hours. E.g. Mon–Fri, 9am–5pm.
- **24/7 — always on** — function is continuously active. Most expensive. Use for critical monitoring functions.

**Global vs per-function:**
The global pipeline schedule is the default for all functions. Individual functions can override this with their own schedule (you'll see "↳ overrides global" in the Execution panel).

**Managing schedules:**
- **⏱ Schedule toolbar button** — Execution & Schedules panel (per function, inline)
- **Sidebar → Schedules** — full-page overview of all functions, their schedules, and token usage`
  },
  {
    id: 'sc-2', category: 'Schedules', title: 'Choosing the right schedule',
    content: `**Use "Triggered only" for:**
- Functions that respond to inbound requests (support tickets, form submissions)
- Functions triggered by other functions in the pipeline
- Development/testing to avoid unexpected charges

**Use "Custom schedule" for:**
- Business-hours only functions (marketing team, customer service)
- Weekly reporting agents (Mon 8am–9am)
- Scheduled batch processing (e.g. overnight data sync)

**Use "24/7" for:**
- Security monitoring
- Customer-facing chatbots that must be always available
- Alert systems that need sub-minute response times

**Cost impact:**
A function on 24/7 with a Smart (Opus) agent can cost significantly more than one on a custom schedule. Use the 💰 cost overlay to see estimated spend per function.`
  },

  {
    id: 'ex-1', category: 'Example Pipelines', title: 'Customer support pipeline',
    diagram: <SupportPipelineDiagram />,
    content: `⚠️ **Includes AI agents (coming soon).** This example uses Function agents + playbooks, which aren't enabled yet. The Collections, Triggers, Conditions, Actions, Gates and Connections all work today — for a fully-working example see "Your first pipeline" and the Automation guides.

**Use case:** A small e-commerce team wants to automate tier-1 support while keeping humans in the loop for refunds.

**Configuration:**
- **Service Desk** function: 3 agents (Manager, Tier-1 ×2), Playbook: triage tickets, auto-resolve FAQs, escalate refunds to Gate
- **Gate: Refund Approval**: Approver = "Customer Success Manager", criteria = "Refund requested, verify against policy"
- **Finance** function: 1 agent, Playbook: process approved refunds, update records
- **Support Tickets** collection: fields = Customer, Issue, Status, Priority, Assignee

**Schedule:** Service Desk on Mon–Fri 8am–8pm, Finance on triggered-only`
  },
  {
    id: 'ex-2', category: 'Example Pipelines', title: 'DevOps CI/CD pipeline',
    diagram: <DevOpsPipelineDiagram />,
    content: `⚠️ **Includes AI agents (coming soon)** — Function agents/playbooks aren't enabled yet. The Connections, Gate and automation parts work today.

**Use case:** A dev team wants automated code review, testing, and gated production deployments.

**Configuration:**
- **GitHub Connection**: service = GitHub, endpoint = https://api.github.com, auth token = PAT
- **Development** function: Agents for frontend/backend. Playbook: review PRs, write tests, update tickets
- **Gate: Code Review**: Approver = "Tech Lead", criteria = "All tests passing, no critical lint errors"
- **DevOps** function: Playbook: trigger build, run deploy pipeline, notify on success/failure
- **Deploy Target Connection**: service = Webhook, URL = your deploy webhook

**Schedule:** Development and DevOps on custom Mon–Fri 9am–7pm`
  },
  {
    id: 'ex-3', category: 'Example Pipelines', title: 'Content marketing pipeline',
    diagram: <MarketingPipelineDiagram />,
    content: `⚠️ **Includes AI agents (coming soon)** — Function agents/playbooks aren't enabled yet. The Collection, Gate and Connection parts work today.

**Use case:** A marketing team wants AI to draft social posts for human approval before publishing.

**Configuration:**
- **Marketing** function: 3 agents (Manager, Copywriter, Social Media Scheduler). Playbook: draft posts based on brief, schedule for optimal times, use brand voice
- **Gate: Content Approval**: Approver = "Marketing Director", criteria = "Brand guidelines followed, no pricing mentions", Auto-approve after = 48h
- **Buffer Connection**: service = Webhook, endpoint = Buffer publish webhook

**Schedule:** Marketing on Mon–Fri 9am–6pm (content prep during business hours)`
  },

  {
    id: 'bp-1', category: 'Best Practices', title: 'Designing effective pipelines',
    content: `**Keep functions focused**
One domain per function. A "Marketing & Finance & HR" function will be confused. Split them up and connect with edges.

**Always add a Gate before external actions**
If a function can send emails, post to social media, or spend money — put a Gate before the connection. It's much easier to approve a post than to undo a published one.

**Use Collections for state**
If your pipeline needs to track "what happened" — use a Collection node. Functions should read and write to Collections rather than keeping state in their prompts.

**Test with Triggered only first**
Start all new functions on "Triggered only" schedule. This prevents unexpected costs while you're building and testing.

**Use Notes to document edge cases**
If there's a rule that's not obvious from the node names (e.g. "This gate only applies on weekdays"), add a Note node near it explaining the rule.`
  },
  {
    id: 'bp-2', category: 'Best Practices', title: 'Agent model selection guide',
    diagram: <ModelDiagram />,
    content: `⚠️ **Not yet available** — agent AI models aren't enabled yet. This is a preview for when AI agents go live.

**⚡ Efficient — Haiku 4.5 ($0.80/$4 per M tokens)**
Best for: FAQ responses, ticket classification, simple form filling, repetitive data entry, status updates.

Avoid for: Complex reasoning, nuanced decisions, code review, legal/financial analysis.

**◈ Balanced — Sonnet 4.6 ($3/$15 per M tokens)**
Best for: Most business functions — writing, analysis, project management, customer support, content creation.

The default choice for new agents. Upgrade to Opus only if you find it struggling with specific tasks.

**★ Expert — Opus 4.8 ($15/$75 per M tokens)**
Best for: Complex technical analysis, code review, legal document review, strategic planning, nuanced customer escalations.

Use sparingly — only for agents that genuinely need deep reasoning. One Opus agent per function max.

**Rule of thumb:** Run your pipeline for a week on Sonnet, then review the cost overlay. If a function's cost is high, check whether any agents can be downgraded to Haiku.`
  },
  {
    id: 'bp-3', category: 'Best Practices', title: 'Security best practices',
    content: `**API tokens and credentials**
- Never hardcode API tokens in playbooks or node descriptions
- Use the Connection node's auth token field for integration credentials
- For production, use the Connections portal (sidebar) which stores credentials in Firebase Secret Manager (encrypted at rest)
- Rotate tokens regularly — if a token is compromised, delete the connection in the portal and create a new one

**Access control**
- Use workspace isolation — create separate workspaces for different teams or sensitivity levels
- The Settings page allows admin-only API key management

**Gate nodes as a safety net**
- Put Gate nodes before any function that can take irreversible actions (send emails, post publicly, process payments)
- Configure a human approver — never use auto-approve on financial or public-facing gates

**Data in Collections**
- Collections store data in Firestore — it's encrypted in transit and at rest
- Don't store passwords or full payment card numbers in Collection records
- Use reference fields to link to external systems rather than duplicating sensitive data`
  },

  // ── Workspace & Team ──────────────────────────────────────────
  {
    id: 'wt-1', category: 'Workspace & Team', title: 'Roles & permissions',
    content: `Every workspace member has a **role** that controls what they can do. Roles are managed under **Workspace settings → Members** (you need the *Manage members* permission, which Owners and Admins have).

**Built-in roles:**
- **Owner** — full access, including transferring ownership. The workspace creator.
- **Admin** — full access except ownership transfer.
- **Editor** — edit the pipeline, manage records, run workflows, approve gates.
- **Approver** — view records, approve/reject gates, read the knowledge base.
- **Service Desk** — view and update record values, read the knowledge base.
- **Viewer** — read-only access.

**How it's enforced:** Permissions are checked both in the UI (controls are hidden or disabled) and on the server (Cloud Functions reject unauthorized calls). A read-only user who opens a node sees a 🔒 banner and disabled fields.

**Inviting someone:** In Members, enter their email, pick a role, and click Add member. They must already have a Projectly account.`
  },
  {
    id: 'wt-2', category: 'Workspace & Team', title: 'The Inbox',
    content: `The **Inbox** is where messages and approval requests generated by your pipeline land for a human to act on.

**What shows up here:**
- **Send message** actions targeting the Inbox channel
- **Gate approvals** — requests waiting on a human decision

**Working the inbox:**
- Click a thread to read the full context and reply
- Right-click for quick actions (mark read, archive, bulk-select)
- Approve or reject gate requests directly from the relevant item

**Tip:** The Inbox is the primary surface for Approvers — they can clear pending gate approvals here without touching the pipeline canvas.`
  },
  {
    id: 'wt-3', category: 'Workspace & Team', title: 'Knowledge Base',
    content: `The **Knowledge Base** is auto-generated documentation of your pipeline's structure. It gives non-technical team members a readable map of how work flows.

**What it covers:**
- Each Function (and, once AI is enabled, its agents and playbook)
- Collections and their fields
- Gates and their approval rules
- Connections in use

**When to use it:** Onboarding a new team member, reviewing what a pipeline does before editing it, or sharing how a process works with a stakeholder who shouldn't edit the canvas.

The Knowledge Base updates automatically as you change the pipeline — there's nothing to maintain by hand.`
  },
  {
    id: 'wt-4', category: 'Workspace & Team', title: 'Reports & cost tracking',
    content: `The **Reports** page turns your pipeline's real execution history into metrics. It reads from the per-workspace \`pipeline_logs\` so the numbers reflect what actually ran.

**What you can see today:**
- Workflow runs over time, grouped by trigger and status
- Most active automations and busiest collections

**AI cost & token usage (coming soon):** Once AI agents are enabled, Reports also breaks down cost in USD and tokens by model and function. Until then there's no AI usage to report, so those sections stay empty.`
  },

  // ── External API ──────────────────────────────────────────────
  {
    id: 'api-1', category: 'External API', title: 'Getting started with the API',
    content: `The Projectly REST API lets external applications read and write **data collection records** in your workspace. When a record is created or updated via the API, any pipeline automations attached to that collection fire automatically — exactly as if the change were made inside Projectly.

**Step 1 — Generate an API key**
Open your user menu (bottom-left) and click **Workspace settings**. Under the **API Keys** section, give your key a name (e.g. "Marketing site") and pick an expiry. Click **Generate key**.

**Step 2 — Copy the key**
Your key is shown **once**. Copy it immediately and store it somewhere secure — a password manager or your server's environment variables. Projectly only stores a SHA-256 hash of the key; it can never be recovered.

**Step 3 — Make your first request**
\`\`\`
curl -H "Authorization: Bearer pk_your_key_here" \\
     https://projectly-dev.web.app/api/v1/collections
\`\`\`

This returns all collections in the workspace. From there you can list records, create new ones, and update or delete existing ones.

**Base URL**: \`https://projectly-dev.web.app/api/v1\`
**Auth**: \`Authorization: Bearer pk_…\` header on every request.`
  },
  {
    id: 'api-2', category: 'External API', title: 'API reference — endpoints',
    content: `All endpoints require a valid API key in the \`Authorization: Bearer pk_…\` header.

**List collections**
\`GET /api/v1/collections\`
Returns \`{ collections: [{ id, name, fields, recordCount }] }\`.

**Get collection details**
\`GET /api/v1/collections/:collectionId\`
Returns a single collection with its field schema and record count.

**List records**
\`GET /api/v1/collections/:collectionId/records\`
Returns \`{ records: [{ id, values }], total }\`.

**Get a single record**
\`GET /api/v1/collections/:collectionId/records/:recordId\`
Returns \`{ record: { id, values } }\`.

**Create a record**
\`POST /api/v1/collections/:collectionId/records\`
Body: \`{ "values": { "fieldId1": "value", "fieldId2": "value" } }\`
Returns \`{ record: { id, values } }\` with status 201. Unknown field ids are rejected with a 400 error listing valid fields.

**Update a record**
\`PATCH /api/v1/collections/:collectionId/records/:recordId\`
Body: \`{ "values": { "fieldId": "new value" } }\`
Only the fields you include are updated; others are left unchanged.

**Delete a record**
\`DELETE /api/v1/collections/:collectionId/records/:recordId\`
Returns \`{ deleted: true }\`.`
  },
  {
    id: 'api-3', category: 'External API', title: 'API keys — expiry, revoke, security',
    content: `**Key lifecycle**
- Keys are generated in **Workspace Settings** (user menu → Workspace settings).
- Choose an expiry: 7 days, 30 days, 90 days, 1 year, or no expiry.
- The raw key is shown **once** at creation. Store it securely.
- Only the SHA-256 hash is stored — Projectly can never recover the original.

**Revoking a key**
Click **Revoke** next to any active key. The key stops working instantly — any integration using it will start receiving 401 errors. This cannot be undone; generate a new key instead.

**Security best practices**
- Store keys in environment variables or a secrets manager — never in frontend code or git repositories.
- Use short-lived keys (30 or 90 days) for integrations you actively maintain; use longer expiry only for stable production services.
- Each key is scoped to a single workspace. Create separate keys for separate integrations so you can revoke one without breaking the others.
- The API only exposes **collection records** — it does not grant access to pipeline configuration, agents, connections, or other workspace settings.
- Every API call increments a \`callCount\` on the key for future usage tracking and billing.`
  },
  {
    id: 'api-4', category: 'External API', title: 'Triggering automations via the API',
    content: `One of the most powerful aspects of the API is that **writing records triggers automations automatically**.

**How it works**
When you POST a new record or PATCH an existing one through the API, the change is saved to the pipeline document in Firestore. Projectly's \`onPipelineChange\` Cloud Function detects the change and runs any trigger nodes attached to that collection:

- **record_added** triggers fire when the API creates a new record (POST).
- **field_change** triggers fire when the API updates a field to a specific value (PATCH).

**Example: external form → pipeline**
1. Build a contact form on your website.
2. When a user submits, POST a new record to your "Leads" collection via the API.
3. A \`record_added\` trigger attached to "Leads" kicks off your pipeline — the AI function processes the lead, the gate waits for approval, the action sends a welcome email.

**No action endpoints needed**
You never need to call pipeline actions directly. Just write to the collection that has a trigger, and the pipeline does the rest. This keeps the API surface small and secure.

**Latency**
The API responds as soon as the record is saved. The trigger and workflow run asynchronously in the background. Check the Logs page to see when each step completes.`
  },

];

const CATEGORIES = [...new Set(ARTICLES.map(a => a.category))];

function inlineFormat(text, prefix = '') {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
    p.startsWith('**') ? <strong key={`${prefix}b${j}`}>{p.slice(2, -2)}</strong> : p
  );
  return parts.map((p, j) => {
    if (typeof p !== 'string') return p;
    return p.split(/(`[^`]+`)/g).map((s, k) =>
      s.startsWith('`') ? <code key={`${prefix}c${j}${k}`} className="uni-inline-code">{s.slice(1, -1)}</code> : s
    );
  });
}

function renderContent(text) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('```')) {
      const block = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) { block.push(lines[i]); i++; }
      elements.push(<pre key={`code${i}`} className="uni-code-block">{block.join('\n')}</pre>);
      i++; continue;
    }
    // Markdown tables
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      const isSep = r => /^\|[\s|:−—-]+\|$/.test(r);
      const cells = r => r.slice(1, -1).split('|').map(c => c.trim());
      const rows = tableLines.filter(r => !isSep(r));
      if (rows.length > 0) {
        const [hdr, ...body] = rows;
        const tblKey = i;
        elements.push(
          <table key={`tbl${tblKey}`} className="uni-table">
            <thead><tr>{cells(hdr).map((c, j) => <th key={j}>{inlineFormat(c, `h${tblKey}${j}`)}</th>)}</tr></thead>
            {body.length > 0 && <tbody>{body.map((r, ri) => <tr key={ri}>{cells(r).map((c, j) => <td key={j}>{inlineFormat(c, `r${tblKey}${ri}${j}`)}</td>)}</tr>)}</tbody>}
          </table>
        );
      }
      continue;
    }
    if (!line.trim()) { i++; continue; }
    elements.push(<p key={`p${i}`} className="uni-para">{inlineFormat(line, `l${i}`)}</p>);
    i++;
  }
  return elements;
}


export default function UniversityView({ onNavigate, initialArticleId } = {}) {
  const [query,       setQuery]       = useState('');
  const [activeId,    setActiveId]    = useState('gs-1');
  const [activeGroup, setActiveGroup] = useState('Getting Started');

  // Deep-link: when arriving from a Lab's "Read the concept" link, open that
  // article (and expand its category) instead of the default landing page.
  useEffect(() => {
    if (!initialArticleId) return;
    const target = ARTICLES.find(a => a.id === initialArticleId);
    if (target) { setActiveId(target.id); setActiveGroup(target.category); setQuery(''); }
  }, [initialArticleId]);

  const getSearchText = (a) => [a.title, a.category, a.content || ''].join(' ').toLowerCase();

  const filtered = query.trim()
    ? ARTICLES.filter(a => getSearchText(a).includes(query.toLowerCase()))
    : ARTICLES;

  const active = ARTICLES.find(a => a.id === activeId) || filtered[0];

  return (
    <div className="uni-layout">
      <div className="uni-sidebar">
        <div className="uni-sidebar-top">
          <div className="uni-logo">🎓 Projectly University</div>
          <input
            className="uni-search"
            placeholder="Search guides…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <nav className="uni-nav">
          {(query ? [...new Set(filtered.map(a => a.category))] : CATEGORIES).map(cat => (
            <div key={cat} className="uni-nav-group">
              {!query && (
                <button
                  className={`uni-cat-header${activeGroup === cat ? ' active' : ''}`}
                  onClick={() => setActiveGroup(cat)}
                >
                  {cat}
                </button>
              )}
              {(query || activeGroup === cat) && filtered.filter(a => a.category === cat).map(a => (
                <button
                  key={a.id}
                  className={`uni-nav-item${activeId === a.id ? ' active' : ''}`}
                  onClick={() => { setActiveId(a.id); setActiveGroup(cat); setQuery(''); }}
                >
                  {a.diagram ? '◈ ' : ''}{a.title}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </div>

      <div className="uni-content">
        {active ? (
          <>
            <div className="uni-breadcrumb">{active.category}</div>
            <h1 className="uni-title">{active.title}</h1>
            {onNavigate && ARTICLE_TO_LAB[active.id] && (
              <button
                className="uni-crosslink"
                onClick={() => onNavigate('labs', ARTICLE_TO_LAB[active.id])}
                title="Open the hands-on lab for this concept"
              >
                🧪 Try this hands-on in Labs →
              </button>
            )}
            {active.diagram && (
              <div className="uni-diagram-section">{active.diagram}</div>
            )}
            <div className="uni-body">{renderContent(active.content || '')}</div>
            <div className="uni-footer">
              <span className="uni-footer-note">Projectly University — guides are updated as features ship</span>
            </div>
          </>
        ) : (
          <div className="uni-empty">No articles match your search.</div>
        )}
      </div>
    </div>
  );
}
